import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  ArrowLeft,
  ChevronDown,
  CircleHelp,
  Compass,
  Film,
  FolderOpen,
  MoreHorizontal,
  PencilLine,
  RotateCcw,
  Save,
  Sparkles,
  Undo2,
  Redo2,
  Volume2,
  VolumeX,
} from 'lucide-preact';
import { createCoasterRuntime } from '../app/runtime.ts';
import type {
  CoasterRuntime,
  HeroCaptureBundle,
  PublishingDetails,
  RuntimeSnapshot,
} from '../app/runtime-types.ts';
import {
  createPhase4PlatformAdapter,
  isLocalPhase4QaPlatformAdapter,
  type CloudDraftRecord,
  type CloudDraftSummary,
  type CoasterViewer,
  type DiscoveryCatalogPage,
  type MyCoasterSummary,
  type PublicCatalogSort,
  type PublicCoasterCard,
  type PublishStatusRecord,
  type PublicCoasterRecord,
  type ReadOnlyShare,
  type ReadOnlyShareRecord,
  buildEditorResetPath,
  buildPublicCoasterUrl,
  buildReadOnlyShareUrl,
  mergeDiscoveryCatalog,
  ownerProjectStatusLabel,
  readLocalQaUrlContext,
  resolvePublicBaseUrl,
} from '../platform/index.ts';
import {
  createNamedProjectRepository,
  createDraftPreview,
  DraftPreviewRepository,
  localQaDraftPersistenceScope,
  type DraftPersistenceScope,
} from '../persistence/index.ts';
import {
  CreatorPublishWorkspaceRepository,
  createCloudSyncFingerprint,
  createEmptyPublishWorkspace,
  createHeroCameraFingerprint,
  createVisualProjectFingerprint,
  type CreatorPublishWorkspaceV1,
  type PublishConflictState,
  type PublishHydrationState,
} from '../publishing/index.ts';
import type { CoasterProject } from '../schema/index.ts';
import { EditorShell } from './EditorShell.tsx';
import { PublishPanel, type PublishPanelStatus } from './PublishPanel.tsx';
import { VisitorLaunchPanel } from './VisitorLaunchPanel.tsx';
import { MediaStudio } from './MediaStudio.tsx';
import {
  BrowsePanel,
  MyCoastersPanel,
  type BrowseCollection,
  type HubView,
} from './DiscoveryHub.tsx';
import type * as ReactionCamUi from './ReactionCam.tsx';
import { Button, CloseButton, Dialog, IconButton, StatusPill } from './primitives.tsx';
import { UI_COPY } from './ui-copy.ts';

const INITIAL_SNAPSHOT: RuntimeSnapshot = {
  mode: 'menu',
  ready: false,
  editorRevision: 0,
  status: 'Loading coaster…',
  selectedNodeId: null,
  selectedNodeIds: [],
  selectedNodeLabel: null,
  selectedNodeIndex: null,
  nodeIds: [],
  nodeCount: 0,
  selectedHeightM: null,
  selectedPosition: null,
  selectedBankDegrees: null,
  selectedRollWindingDegrees: null,
  selectedRollWindingMixed: false,
  selectedSectionId: null,
  selectedSectionIndex: null,
  selectedSectionLabel: null,
  selectedSectionBehavior: null,
  autoBankPending: false,
  sectionKinds: [],
  canUndo: false,
  canRedo: false,
  canDeleteLastPiece: false,
  finishChecking: false,
  stationPlacement: 'near',
  fixtureId: 'lift-drop',
  fixtureTitle: 'Lift & Drop Lab',
  activeTool: 'build',
  editorMode: 'basic',
  selectionTarget: 'piece',
  snappingEnabled: true,
  activeOverlay: 'none',
  saveState: 'clean',
  hasSavedDraft: false,
  hasUnsavedChanges: false,
  topologyStatus: 'closed',
  constructionState: 'closed_ready',
  buildEnd: null,
  plotBounds: { minX: -70, maxX: 70, minZ: -50, maxZ: 50, minY: 1, maxY: 65 },
  testAllowed: false,
  testDisabledReason: 'Checking your ride.',
  validation: {
    revision: 0,
    issues: [],
    samples: [],
    blockerCount: 0,
    warningCount: 0,
    testAllowed: false,
    summary: 'Checking track',
  },
  validationPending: true,
  preview: null,
  appearance: {
    themeId: 'park_model',
    trackColor: '#2457b2',
    supportColor: '#343a40',
    trainColor: '#d94a38',
  },
  supportsVisible: true,
  sceneryVisible: true,
  sceneryCount: 0,
  selectedSceneryId: null,
  selectedSceneryIndex: null,
  selectedSceneryRotationDegrees: null,
  drafts: [],
  lastOpenedProjectId: null,
  recoveredDraft: null,
  tutorial: { visible: true, step: 1, kind: 'template' },
  telemetry: {
    speedMps: 0,
    speedMph: 0,
    verticalG: 1,
    lateralG: 0,
    longitudinalG: 0,
    peakSpeedMph: 0,
    activeSectionType: null,
    activeSectionLabel: 'Coasting',
  },
  ride: {
    phase: 'idle',
    progressPercent: 0,
    elapsedSeconds: 0,
    dispatchSecondsRemaining: 0,
    peakVerticalG: 1,
    peakVerticalProgressPercent: 0,
    minVerticalG: 1,
    minVerticalProgressPercent: 0,
    peakLateralG: 0,
    peakLateralProgressPercent: 0,
    intensity: {
      level: 'comfortable',
      label: 'Comfortable',
      message: 'Forces stayed in the initial comfort band.',
      progressPercent: 0,
    },
    visitedSectionTypes: [],
    photoFlash: false,
  },
  lastRide: null,
  fullValidation: {
    pending: false,
    status: 'not_run',
    outcome: 'not_run',
    testPolicy: 'blocked',
    publishReady: false,
    technicalEligible: false,
    intensity: null,
    topSpeedMph: null,
    maxVerticalG: null,
    minVerticalG: null,
    maxAbsLateralG: null,
    maxAbsLongitudinalG: null,
    issues: [],
  },
  replay: {
    available: false,
    paused: false,
    elapsedSeconds: 0,
    durationSeconds: 0,
    progressPercent: 0,
  },
  media: {
    available: false,
    requiresNewRide: false,
    open: false,
    timelineSource: null,
    selectedAlternative: 0,
    editLabels: ['Best Moments', 'Biggest Thrills', 'Front Row'],
    videoAvailable: false,
    photoSource: null,
    selectedPosterId: null,
    posterCandidates: [],
    creatorPosterSkipped: false,
    photoUrl: null,
    cardUrl: null,
    videoUrl: null,
    loopUrl: null,
    status: 'idle',
    progress: 0,
    includeAudio: true,
    includeReaction: false,
    includeReactionMicrophone: false,
    reactionAvailable: false,
    reactionMicrophoneAvailable: false,
    reactionRawUrl: null,
    reactionPlacement: 'top-right',
    reactionMirrored: false,
    imageCopyAvailable: false,
    publicLinkAvailable: false,
    formatLabel: null,
    message: 'Complete a ride to create a highlight.',
    shareOutcome: 'idle',
  },
  reaction: {
    eligibility: {
      state: 'platform_unavailable',
      reason: 'Reaction Cam is not enabled for this runtime.',
    },
    setupOpen: false,
    phase: 'idle',
    includeMicrophone: false,
    microphoneIncluded: false,
    exportMirrored: false,
    placement: 'top-right',
    devices: [],
    selectedDeviceId: null,
    previewStream: null,
    countdown: 0,
    recordingElapsedSeconds: 0,
    rawReviewUrl: null,
    rawAvailable: false,
    availableForExport: false,
    rideSpeedLocked: false,
    message: 'Reaction Cam is optional.',
    interruptionReason: null,
  },
  settings: {
    cameraFov: 62,
    cameraShake: 0.25,
    reducedMotion: false,
    masterVolume: 0.75,
    muted: false,
    audioState: 'locked',
    testSpeed: 1,
  },
  diagnostics: {
    qualityTier: 'high',
    frameMsP50: 0,
    frameMsP95: 0,
    inputMsP50: 0,
    inputMsP95: 0,
    rebuildMs: 0,
    changedSpans: 0,
    geometryRevision: 0,
    trackLengthM: 0,
    sampleCount: 0,
    rendererGeometries: 0,
    rendererTextures: 0,
    rideCycles: 0,
    retainedTraceSamples: 0,
    retainedMediaSamples: 0,
    mediaArtifactUrls: 0,
    mediaExportActive: false,
    reactionActiveTracks: 0,
    reactionRecorderActive: false,
    reactionRetainedBytes: 0,
    validationActive: 0,
    validationQueued: 0,
    audioActiveVoices: 0,
    listenerCount: 0,
    maxRailSeamGapM: 0,
    maxRailSeamAngleDegrees: 0,
    maxTieSpacingM: 0,
  },
  debugVisible: window.location.hash === '#/debug',
  persistenceAvailable: true,
  persistenceTier: 'durable',
};

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function mph(value: number): string {
  return Number.isFinite(value) ? `${value.toFixed(1)} mph` : '--';
}

function force(value: number): string {
  return Number.isFinite(value) ? `${value.toFixed(2)} G` : '--';
}

function TestSpeedControl({
  value,
  runtime,
  locked = false,
}: {
  readonly value: RuntimeSnapshot['settings']['testSpeed'];
  readonly runtime: CoasterRuntime | null;
  readonly locked?: boolean;
}) {
  return (
    <div class="test-speed-control" role="group" aria-label="Simulation speed">
      <span>Simulation Speed</span>
      {([0.5, 1, 2, 4] as const).map((speed) => (
        <button
          type="button"
          aria-pressed={value === speed}
          data-testid={`test-speed-${speed}`}
          disabled={locked}
          title={locked ? 'Reaction Cam rides run at 1× for synchronization.' : undefined}
          onClick={() => runtime?.setTestSpeed(speed)}
        >
          {speed}×
        </button>
      ))}
      {locked && <small>Reaction Cam stays at 1×</small>}
    </div>
  );
}

export function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const projectNameRef = useRef<HTMLInputElement>(null);
  const runtimeRef = useRef<CoasterRuntime | null>(null);
  const [state, setState] = useState<RuntimeSnapshot>(INITIAL_SNAPSHOT);
  const [reactionUi, setReactionUi] = useState<typeof ReactionCamUi | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);
  const [confirmPublishSave, setConfirmPublishSave] = useState(false);
  const [topbarMenuOpen, setTopbarMenuOpen] = useState(false);
  const [rideMenuOpen, setRideMenuOpen] = useState(false);
  const [rideResultMenuOpen, setRideResultMenuOpen] = useState(false);
  const [coordinateRecoveryEpoch, setCoordinateRecoveryEpoch] = useState(0);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [hubView, setHubView] = useState<HubView>('home');
  const [catalogPage, setCatalogPage] = useState<DiscoveryCatalogPage | null>(null);
  const [catalogSort, setCatalogSort] = useState<PublicCatalogSort>('most_boosted');
  const [browseCollection, setBrowseCollection] = useState<BrowseCollection>('most_boosted');
  const [remixableOnly, setRemixableOnly] = useState(false);
  const [catalogCreator, setCatalogCreator] = useState<{ id: string; label: string } | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogMessage, setCatalogMessage] = useState<string | null>(null);
  const [socialPendingIds, setSocialPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [myCoasters, setMyCoasters] = useState<readonly MyCoasterSummary[]>([]);
  const [myCoastersTab, setMyCoastersTab] = useState<'published' | 'drafts'>('published');
  const [myCoastersLoading, setMyCoastersLoading] = useState(false);
  const [myCoastersError, setMyCoastersError] = useState<string | null>(null);
  const [draftPreviewUrls, setDraftPreviewUrls] = useState<Readonly<Record<string, string>>>({});
  const draftPreviewUrlsRef = useRef<Readonly<Record<string, string>>>({});
  const [confirmDeleteProjectId, setConfirmDeleteProjectId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string | null>(null);
  const localQaContext = useMemo(
    () =>
      import.meta.env.MODE !== 'production' ? readLocalQaUrlContext(window.location.search) : null,
    [],
  );
  const draftPersistenceScope = useMemo<DraftPersistenceScope | undefined>(
    () =>
      localQaContext
        ? localQaDraftPersistenceScope(
            localQaContext.session,
            localQaContext.viewer,
            localQaContext.viewer === 'qa.alice' ? 'Alice QA' : 'Bob QA',
          )
        : undefined,
    [localQaContext],
  );
  const draftPreviewRepository = useMemo(
    () => new DraftPreviewRepository(draftPersistenceScope),
    [draftPersistenceScope],
  );
  const publicBaseUrl = useMemo(
    () =>
      resolvePublicBaseUrl({
        currentHref: window.location.href,
        configuredBaseUrl:
          import.meta.env.VITE_BUNCHA_PUBLIC_URL ||
          new URL(import.meta.env.BASE_URL, window.location.origin).toString(),
        localQa: localQaContext,
      }),
    [localQaContext],
  );
  const editorResetPath = useMemo(
    () =>
      buildEditorResetPath({
        currentHref: window.location.href,
        basePath: import.meta.env.BASE_URL,
        localQa: localQaContext,
      }),
    [localQaContext],
  );
  const platform = useMemo(
    () =>
      createPhase4PlatformAdapter({
        apiBaseUrl: import.meta.env.VITE_COASTERS_API_URL || window.location.origin,
        buncha: window.buncha,
        allowLocalQa: import.meta.env.MODE !== 'production',
      }),
    [],
  );
  const [viewer, setViewer] = useState<CoasterViewer>({
    kind: 'anonymous',
    id: null,
    displayName: 'Guest',
  });
  const publishWorkspaceOwnerId = viewer.kind === 'authenticated' ? viewer.id : 'local-device';
  const publishWorkspaceRepository = useMemo(
    () =>
      new CreatorPublishWorkspaceRepository(
        createNamedProjectRepository({ scope: draftPersistenceScope }),
        publishWorkspaceOwnerId,
      ),
    [draftPersistenceScope, publishWorkspaceOwnerId],
  );
  const [cloudDrafts, setCloudDrafts] = useState<readonly CloudDraftSummary[]>([]);
  const [cloudRecord, setCloudRecord] = useState<CloudDraftRecord | null>(null);
  const [publishStatus, setPublishStatus] = useState<PublishStatusRecord | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishSessionId, setPublishSessionId] = useState(0);
  const [publishHydration, setPublishHydration] = useState<PublishHydrationState>('idle');
  const [publishProject, setPublishProject] = useState<CoasterProject | null>(null);
  const [publishMessage, setPublishMessage] = useState<string | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [cloudConflict, setCloudConflict] = useState<PublishConflictState | null>(null);
  const [heroCapture, setHeroCapture] = useState<HeroCaptureBundle | null>(null);
  const [heroPreviewUrl, setHeroPreviewUrl] = useState<string | null>(null);
  const [heroCaptureState, setHeroCaptureState] = useState<'none' | 'captured' | 'stale'>('none');
  const [readOnlyShare, setReadOnlyShare] = useState<ReadOnlyShare | null>(null);
  const [publishWorkspace, setPublishWorkspace] = useState<CreatorPublishWorkspaceV1 | null>(null);
  const [visitor, setVisitor] = useState<
    | { kind: 'public'; record: PublicCoasterRecord }
    | { kind: 'unlisted'; record: ReadOnlyShareRecord }
    | null
  >(null);
  const [activePublicCard, setActivePublicCard] = useState<PublicCoasterCard | null>(null);
  const [activeRemixSource, setActiveRemixSource] = useState<PublicCoasterCard | null>(null);
  const [launchMessage, setLaunchMessage] = useState<string | null>(null);
  const launchAttempted = useRef(false);
  const referralRide = useRef<{
    referralId: string | null;
    rideSessionId: string | null;
    started: boolean;
    completed: boolean;
  } | null>(null);
  const lastCloudSyncedEditorRevision = useRef<number | null>(null);
  const publishHydrationToken = useRef(0);
  const cloudRecordRef = useRef<CloudDraftRecord | null>(null);
  const cloudWriteTail = useRef<Promise<void>>(Promise.resolve());
  const publishWorkspaceWriteTail = useRef<Promise<void>>(Promise.resolve());
  const enqueueCloudWrite = useCallback(<T,>(operation: () => Promise<T>): Promise<T> => {
    const queued = cloudWriteTail.current.then(operation, operation);
    cloudWriteTail.current = queued.then(
      () => undefined,
      () => undefined,
    );
    return queued;
  }, []);
  const inLiveRide = state.mode === 'ride-first-person' || state.mode === 'ride-third-person';
  const inReplay = state.mode === 'replay';
  const inRide = inLiveRide || inReplay;
  const inEditor = state.mode === 'editor';
  const camera = state.mode === 'ride-third-person' ? 'third-person' : 'first-person';
  const waitingForDispatch =
    inLiveRide && state.telemetry.activeSectionType === 'station' && state.telemetry.speedMps < 0.3;
  const reactionCamAvailable = state.reaction.eligibility.state === 'available';

  useEffect(() => {
    if (reactionUi || (!state.reaction.setupOpen && state.reaction.phase !== 'recording')) return;
    let cancelled = false;
    void import('./ReactionCam.tsx').then((module) => {
      if (!cancelled) setReactionUi(module);
    });
    return () => {
      cancelled = true;
    };
  }, [reactionUi, state.reaction.phase, state.reaction.setupOpen]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const runtime = createCoasterRuntime(canvasRef.current, {
      persistenceScope: draftPersistenceScope,
      reactionCamPlatformAvailable:
        import.meta.env.MODE !== 'production' ||
        platform.getCapability('camera').availability === 'available',
      reactionCamEligibleAccount: true,
      reactionCamSynthetic:
        import.meta.env.MODE !== 'production' &&
        new URLSearchParams(window.location.search).get('reactionCam') === 'synthetic',
    });
    runtimeRef.current = runtime;
    const unsubscribe = runtime.subscribe(setState);
    return () => {
      unsubscribe();
      runtime.dispose();
      runtimeRef.current = null;
    };
  }, [draftPersistenceScope, platform]);

  useEffect(() => {
    let cancelled = false;
    const loadPreviews = async () => {
      const entries = await Promise.all(
        state.drafts
          .filter((draft) => !draft.corrupt)
          .map(
            async (draft) =>
              [draft.projectId, await draftPreviewRepository.load(draft.projectId)] as const,
          ),
      );
      if (cancelled) return;
      const next: Record<string, string> = {};
      for (const [projectId, blob] of entries)
        if (blob) next[projectId] = URL.createObjectURL(blob);
      setDraftPreviewUrls((current) => {
        Object.values(current).forEach((url) => URL.revokeObjectURL(url));
        draftPreviewUrlsRef.current = next;
        return next;
      });
    };
    void loadPreviews();
    return () => {
      cancelled = true;
    };
  }, [draftPreviewRepository, state.drafts]);

  useEffect(
    () => () => {
      draftPreviewRepository.dispose();
      Object.values(draftPreviewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    },
    [draftPreviewRepository],
  );

  const hasPendingNameChange = renameValue !== null && renameValue !== state.fixtureTitle;

  useEffect(() => {
    if ((!state.hasUnsavedChanges && !hasPendingNameChange) || !inEditor || visitor) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasPendingNameChange, inEditor, state.hasUnsavedChanges, visitor]);

  useEffect(() => {
    let cancelled = false;
    void platform.getViewer().then((result) => {
      if (cancelled || !result.ok) return;
      setViewer(result.value);
      if (result.value.kind === 'authenticated') {
        void platform.listCloudDrafts().then((drafts) => {
          if (!cancelled && drafts.ok) setCloudDrafts(drafts.value);
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [platform]);

  useEffect(() => {
    if (!publishOpen || publishHydration === 'loading' || !runtimeRef.current) return;
    setPublishProject(runtimeRef.current.getProjectSnapshot());
  }, [publishHydration, publishOpen, state.editorRevision]);

  useEffect(() => {
    return () => {
      if (heroPreviewUrl) URL.revokeObjectURL(heroPreviewUrl);
    };
  }, [heroPreviewUrl]);

  useEffect(() => {
    if (!state.ready || !runtimeRef.current || launchAttempted.current) return;
    const url = new URL(window.location.href);
    const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
    const routePath =
      basePath && url.pathname.startsWith(`${basePath}/`)
        ? url.pathname.slice(basePath.length)
        : url.pathname;
    const publicId = url.searchParams.get('coaster');
    const slugMatch = routePath.match(/^\/coasters\/([^/]+)$/);
    const shareMatch = routePath.match(/^\/coasters\/share\/([^/]+)$/);
    const strictShare = new URLSearchParams(url.hash.slice(1)).get('share');
    const strictSeparator = strictShare?.indexOf('.') ?? -1;
    const strictShareId =
      strictShare && strictSeparator > 0 ? strictShare.slice(0, strictSeparator) : null;
    const strictSecret =
      strictShare && strictSeparator > 0 ? strictShare.slice(strictSeparator + 1) : null;
    if (!publicId && !slugMatch && !shareMatch && !strictShareId) return;
    launchAttempted.current = true;
    setLaunchMessage('Loading coaster…');
    const load = async () => {
      if (shareMatch || strictShareId) {
        const shareId = strictShareId ?? decodeURIComponent(shareMatch?.[1] ?? '');
        const secret = strictSecret ?? new URLSearchParams(url.hash.slice(1)).get('cap');
        if (!secret) {
          setLaunchMessage('This private link is incomplete. Ask the creator for the full link.');
          return;
        }
        const result = await platform.loadReadOnlyShare(shareId, secret);
        if (!result.ok) {
          setLaunchMessage('This private coaster link is unavailable or has been revoked.');
          return;
        }
        runtimeRef.current?.openEditor();
        await runtimeRef.current?.loadProject(result.value.project, 'readonly_private');
        setVisitor({ kind: 'unlisted', record: result.value });
        setLaunchMessage(null);
        return;
      }
      const result = await platform.getPublicCoaster(
        publicId
          ? { publicCoasterId: publicId }
          : { publicSlug: decodeURIComponent(slugMatch?.[1] ?? '') },
      );
      if (!result.ok) {
        setLaunchMessage('This coaster is private, unavailable, or no longer published.');
        return;
      }
      runtimeRef.current?.openEditor();
      await runtimeRef.current?.loadProject(result.value.project, 'readonly_public');
      setVisitor({ kind: 'public', record: result.value });
      const socialPage = await platform.listPublicCoasters({
        sort: 'most_boosted',
        remixableOnly: false,
        creatorId: result.value.creator.publicCreatorId,
        limit: 18,
      });
      const viewerStates = socialPage.ok
        ? await platform.getViewerCoasterStates(
            socialPage.value.items.map((card) => card.publicCoasterId),
          )
        : null;
      const discoveryPage = socialPage.ok
        ? mergeDiscoveryCatalog(socialPage.value, viewerStates?.ok ? viewerStates.value : null)
        : null;
      setActivePublicCard(
        discoveryPage
          ? (discoveryPage.items.find(
              (card) => card.publicCoasterId === result.value.publicCoasterId,
            ) ?? null)
          : null,
      );
      referralRide.current = {
        referralId: url.searchParams.get('ref') ?? url.searchParams.get('shareId'),
        rideSessionId: null,
        started: false,
        completed: false,
      };
      setLaunchMessage(null);
    };
    void load();
  }, [platform, state.ready]);

  useEffect(() => {
    const referral = referralRide.current;
    if (!referral || referral.started || state.ride.phase !== 'running') return;
    referral.started = true;
    void platform
      .startQualifiedRide({ ...(referral.referralId ? { referralId: referral.referralId } : {}) })
      .then((result) => {
        if (result.ok) referral.rideSessionId = result.value.rideSessionId;
      });
  }, [platform, state.ride.phase]);

  useEffect(() => {
    const referral = referralRide.current;
    if (
      !referral ||
      referral.completed ||
      !referral.rideSessionId ||
      state.lastRide?.outcome !== 'completed'
    )
      return;
    referral.completed = true;
    void platform.completeQualifiedRide({ rideSessionId: referral.rideSessionId });
  }, [platform, state.lastRide?.outcome]);

  useEffect(() => {
    if (state.mode !== 'editor') return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches('input, textarea, select')) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) runtimeRef.current?.redo();
        else runtimeRef.current?.undo();
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        runtimeRef.current?.redo();
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        runtimeRef.current?.deleteSelected();
      } else if (event.key.toLowerCase() === 't' && state.testAllowed) {
        event.preventDefault();
        runtimeRef.current?.startRide('first-person');
      } else if (event.key === 'Escape') {
        runtimeRef.current?.cancelPreview();
      } else if (event.key === 'Enter' && state.preview?.canAccept && !target?.matches('button')) {
        event.preventDefault();
        runtimeRef.current?.acceptPreview();
      } else if (event.key === '[') {
        runtimeRef.current?.traverseSelection(-1);
      } else if (event.key === ']') {
        runtimeRef.current?.traverseSelection(1);
      } else if (event.key.toLowerCase() === 'f') {
        runtimeRef.current?.focusSelection();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [state.mode, state.preview?.canAccept, state.testAllowed]);

  const refreshCloudDrafts = async () => {
    const result = await platform.listCloudDrafts();
    if (result.ok) setCloudDrafts(result.value);
  };

  const loadCatalog = useCallback(
    async (append = false) => {
      setCatalogLoading(true);
      setCatalogError(null);
      const cursor = append ? (catalogPage?.nextCursor ?? undefined) : undefined;
      const query = {
        sort: catalogSort,
        remixableOnly,
        creatorId: catalogCreator?.id,
        cursor,
        limit: 18,
      };
      const result =
        browseCollection === 'favorites'
          ? await platform.listFavoriteCoasters(query)
          : await platform.listPublicCoasters(query);
      if (!result.ok) {
        setCatalogError(result.error.message);
        setCatalogLoading(false);
        return;
      }
      const viewerStates =
        viewer.kind === 'authenticated'
          ? await platform.getViewerCoasterStates(
              result.value.items.map((card) => card.publicCoasterId),
            )
          : null;
      const merged = mergeDiscoveryCatalog(
        result.value,
        viewerStates?.ok ? viewerStates.value : null,
      );
      setCatalogPage((current) =>
        append && current ? { ...merged, items: [...current.items, ...merged.items] } : merged,
      );
      setCatalogLoading(false);
    },
    [
      browseCollection,
      catalogCreator?.id,
      catalogPage?.nextCursor,
      catalogSort,
      platform,
      remixableOnly,
      viewer.kind,
    ],
  );

  const loadMyCoasters = useCallback(async () => {
    if (viewer.kind !== 'authenticated') return;
    setMyCoastersLoading(true);
    setMyCoastersError(null);
    const published = await platform.listMyCoasters();
    if (published.ok) setMyCoasters(published.value);
    else setMyCoastersError(published.error.message);
    setMyCoastersLoading(false);
  }, [platform, viewer.kind]);

  useEffect(() => {
    if (state.mode !== 'menu') return;
    if (hubView === 'browse') void loadCatalog(false);
    if (hubView === 'my-coasters') void loadMyCoasters();
  }, [browseCollection, catalogCreator?.id, catalogSort, hubView, remixableOnly, state.mode]);

  const updateCatalogCard = useCallback(
    (publicCoasterId: string, update: (card: PublicCoasterCard) => PublicCoasterCard) => {
      setCatalogPage((page) =>
        page
          ? {
              ...page,
              items: page.items.map((card) =>
                card.publicCoasterId === publicCoasterId ? update(card) : card,
              ),
            }
          : page,
      );
      setActivePublicCard((card) =>
        card?.publicCoasterId === publicCoasterId ? update(card) : card,
      );
    },
    [],
  );

  const mutateSocial = useCallback(
    async (card: PublicCoasterCard, kind: 'boost' | 'favorite') => {
      if (viewer.kind !== 'authenticated') {
        setCatalogMessage('Sign in through Buncha Games to use Boosts and Favorites.');
        return;
      }
      if (card.viewerState.isOwner) {
        setCatalogMessage('You made this coaster, so its ranking stays organic.');
        return;
      }
      const desired = kind === 'boost' ? !card.viewerState.boosted : !card.viewerState.favorited;
      setSocialPendingIds((current) => new Set([...current, card.publicCoasterId]));
      updateCatalogCard(card.publicCoasterId, (current) => ({
        ...current,
        viewerState: {
          ...current.viewerState,
          [kind === 'boost' ? 'boosted' : 'favorited']: desired,
        },
        stats: {
          ...current.stats,
          ...(kind === 'boost'
            ? { boosts: Math.max(0, current.stats.boosts + (desired ? 1 : -1)) }
            : current.stats.favorites === undefined
              ? {}
              : { favorites: Math.max(0, current.stats.favorites + (desired ? 1 : -1)) }),
        },
      }));
      const result = await (kind === 'boost'
        ? platform.setBoost({
            publicCoasterId: card.publicCoasterId,
            desired,
            idempotencyKey: crypto.randomUUID(),
          })
        : platform.setFavorite({
            publicCoasterId: card.publicCoasterId,
            desired,
            idempotencyKey: crypto.randomUUID(),
          }));
      if (!result.ok) {
        updateCatalogCard(card.publicCoasterId, () => card);
        setCatalogMessage(
          `${kind === 'boost' ? 'Boost' : 'Favorite'} did not save. ${result.error.message}`,
        );
      } else {
        updateCatalogCard(card.publicCoasterId, (current) => ({
          ...current,
          stats: result.value.authoritativeCounts,
          viewerState: {
            ...current.viewerState,
            boosted: result.value.boosted,
            favorited: result.value.favorited,
          },
        }));
        if (kind === 'favorite' && browseCollection === 'favorites' && !result.value.favorited) {
          setCatalogPage((page) =>
            page
              ? {
                  ...page,
                  items: page.items.filter((item) => item.publicCoasterId !== card.publicCoasterId),
                }
              : page,
          );
        }
        setCatalogMessage(
          kind === 'boost'
            ? result.value.boosted
              ? 'Boost added. Popularity cannot be purchased.'
              : 'Boost removed.'
            : result.value.favorited
              ? 'Saved to My Coasters.'
              : 'Removed from Favorites.',
        );
      }
      setSocialPendingIds((current) => {
        const next = new Set(current);
        next.delete(card.publicCoasterId);
        return next;
      });
    },
    [browseCollection, platform, updateCatalogCard, viewer.kind],
  );

  const openPublicCard = useCallback(
    async (card: PublicCoasterCard) => {
      setActiveRemixSource(null);
      setCatalogMessage('Loading coaster…');
      const result = await platform.getPublicCoaster({ publicCoasterId: card.publicCoasterId });
      if (!result.ok) {
        setCatalogMessage(result.error.message);
        return;
      }
      runtimeRef.current?.openEditor();
      await runtimeRef.current?.loadProject(result.value.project, 'readonly_public');
      setVisitor({ kind: 'public', record: result.value });
      setActivePublicCard(card);
      setCatalogMessage(null);
      const destination = buildPublicCoasterUrl({
        publicSlug: card.publicSlug,
        publicBaseUrl,
        localQa: localQaContext,
      });
      const parsed = new URL(destination);
      history.replaceState(null, '', `${parsed.pathname}${parsed.search}${parsed.hash}`);
    },
    [localQaContext, platform, publicBaseUrl],
  );

  const openPublicSlug = useCallback(
    async (publicSlug: string) => {
      setActiveRemixSource(null);
      const result = await platform.getPublicCoaster({ publicSlug });
      if (!result.ok) {
        setMyCoastersError(result.error.message);
        return;
      }
      runtimeRef.current?.openEditor();
      await runtimeRef.current?.loadProject(result.value.project, 'readonly_public');
      setVisitor({ kind: 'public', record: result.value });
      const socialPage = await platform.listPublicCoasters({
        sort: 'most_boosted',
        remixableOnly: false,
        creatorId: result.value.creator.publicCreatorId,
        limit: 18,
      });
      if (socialPage.ok) {
        const viewerStates =
          viewer.kind === 'authenticated'
            ? await platform.getViewerCoasterStates(
                socialPage.value.items.map((card) => card.publicCoasterId),
              )
            : null;
        const discoveryPage = mergeDiscoveryCatalog(
          socialPage.value,
          viewerStates?.ok ? viewerStates.value : null,
        );
        setActivePublicCard(
          discoveryPage.items.find(
            (card) => card.publicCoasterId === result.value.publicCoasterId,
          ) ?? null,
        );
      } else {
        setActivePublicCard(null);
      }
      const destination = buildPublicCoasterUrl({
        publicSlug,
        publicBaseUrl,
        localQa: localQaContext,
      });
      const parsed = new URL(destination);
      history.replaceState(null, '', `${parsed.pathname}${parsed.search}${parsed.hash}`);
    },
    [localQaContext, platform, publicBaseUrl],
  );

  const remixPublicCard = useCallback(
    async (card: PublicCoasterCard) => {
      setSocialPendingIds((current) => new Set([...current, card.publicCoasterId]));
      setCatalogMessage('Creating your remix…');
      try {
        const result = await platform.remixCoaster({ publicCoasterId: card.publicCoasterId });
        if (!result.ok) {
          setCatalogMessage(result.error.message);
          return;
        }
        runtimeRef.current?.openEditor();
        await runtimeRef.current?.loadProject(result.value.draft.project, 'editable_remix');
        setActiveRemixSource(card);
        setCloudRecord(result.value.draft);
        setPublishStatus(null);
        setVisitor(null);
        setActivePublicCard(null);
        referralRide.current = null;
        setCatalogMessage(null);
        history.replaceState(null, '', editorResetPath);
      } finally {
        setSocialPendingIds((current) => {
          const next = new Set(current);
          next.delete(card.publicCoasterId);
          return next;
        });
      }
    },
    [editorResetPath, platform],
  );

  const copyReferralLink = useCallback(
    async (card: PublicCoasterCard) => {
      const fallback = buildPublicCoasterUrl({
        publicSlug: card.publicSlug,
        publicBaseUrl,
        localQa: localQaContext,
      });
      const referral = await platform.createReferralLink(card.publicCoasterId);
      const url = new URL(fallback);
      if (referral.ok) url.searchParams.set('ref', referral.value.referralId);
      try {
        await navigator.clipboard.writeText(url.toString());
        setCatalogMessage(referral.ok ? 'Referral ride link copied.' : 'Ride link copied.');
        setLaunchMessage(referral.ok ? 'Referral ride link copied.' : 'Ride link copied.');
      } catch {
        setCatalogMessage('Copy was blocked. Open the coaster and copy the address instead.');
        setLaunchMessage('Copy was blocked. Copy the address from your browser instead.');
      }
    },
    [localQaContext, platform, publicBaseUrl],
  );

  const refreshPublishStatus = async (projectId: string, announce = false) => {
    const result = await platform.getPublishStatus(projectId);
    if (!result.ok) {
      if (announce) setPublishMessage(result.error.message);
      return null;
    }
    setPublishStatus(result.value);
    setCloudRecord((current) =>
      current?.projectId === projectId
        ? {
            ...current,
            state: result.value.state,
            pendingRevisionId: result.value.pendingRevisionId,
            currentPublicRevisionId: result.value.currentPublicRevisionId,
            publicCoasterId: result.value.publicCoasterId,
            publicSlug: result.value.publicSlug,
          }
        : current,
    );
    if (announce) setPublishMessage('Publish status refreshed.');
    await refreshCloudDrafts();
    return result.value;
  };

  useEffect(() => {
    const projectId = cloudRecord?.projectId;
    if (
      !publishOpen ||
      !projectId ||
      !publishStatus ||
      ownerProjectStatusLabel(publishStatus.state) !== 'pending_review'
    )
      return;
    const timer = window.setInterval(() => {
      void refreshPublishStatus(projectId);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [cloudRecord?.projectId, publishOpen, publishStatus?.state]);

  const replaceHeroCapture = (capture: HeroCaptureBundle | null) => {
    setHeroCapture(capture);
    setHeroPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return capture ? URL.createObjectURL(capture.hero16x9.blob) : null;
    });
  };

  const persistPublishWorkspace = async (
    workspace: CreatorPublishWorkspaceV1,
    options: { clearShare?: boolean } = {},
  ) => {
    const queued = publishWorkspaceWriteTail.current.then(async () => {
      const storedWorkspace = await publishWorkspaceRepository
        .load(workspace.projectId)
        .catch(() => null);
      const currentWorkspace =
        storedWorkspace?.projectId === workspace.projectId &&
        storedWorkspace.ownerId === workspace.ownerId
          ? storedWorkspace
          : null;
      const mergedWorkspace: CreatorPublishWorkspaceV1 = {
        ...(currentWorkspace ?? workspace),
        ...workspace,
        capturedHero: workspace.capturedHero ?? currentWorkspace?.capturedHero ?? null,
        share: options.clearShare ? null : (workspace.share ?? currentWorkspace?.share ?? null),
        updatedAt: new Date().toISOString(),
      };
      await publishWorkspaceRepository.save(mergedWorkspace);
      setPublishWorkspace(mergedWorkspace);
    });
    publishWorkspaceWriteTail.current = queued.then(
      () => undefined,
      () => undefined,
    );
    await queued;
  };

  const hydratePublishContext = async (
    project: CoasterProject,
    options: { showPanel: boolean; knownCloudRecord?: CloudDraftRecord | null },
  ): Promise<boolean> => {
    const token = ++publishHydrationToken.current;
    const editorRevisionAtStart = state.editorRevision;
    setPublishHydration('loading');
    setPublishBusy(true);
    setPublishMessage(null);
    if (options.showPanel) setPublishProject(null);
    try {
      const cloudDraftsAvailable =
        viewer.kind === 'authenticated' &&
        platform.getCapability('cloud_drafts').availability === 'available';
      const cloudResult =
        options.knownCloudRecord !== undefined
          ? null
          : cloudDraftsAvailable
            ? await platform.loadCloudDraft(project.projectId)
            : null;
      if (cloudResult && !cloudResult.ok && cloudResult.error.code !== 'not_found')
        throw new Error(cloudResult.error.message);
      const hydratedCloud =
        options.knownCloudRecord !== undefined
          ? options.knownCloudRecord
          : cloudResult?.ok
            ? cloudResult.value
            : null;
      const statusResult = hydratedCloud
        ? await platform.getPublishStatus(project.projectId)
        : null;
      if (statusResult && !statusResult.ok && statusResult.error.code !== 'not_found')
        throw new Error(statusResult.error.message);

      let workspace = await publishWorkspaceRepository.load(project.projectId);
      let restoredShare: ReadOnlyShare | null = null;
      let hydrationMessage: string | null = null;
      if (workspace?.share) {
        const shareResult = await platform.loadReadOnlyShare(
          workspace.share.shareId,
          workspace.share.secret,
        );
        if (
          shareResult.ok &&
          shareResult.value.projectId === project.projectId &&
          shareResult.value.revisionId === workspace.share.revisionId
        ) {
          restoredShare = workspace.share;
          hydrationMessage = 'Private link restored from this device.';
        } else {
          workspace = { ...workspace, share: null, updatedAt: new Date().toISOString() };
          await publishWorkspaceRepository.save(workspace);
          hydrationMessage = 'The previous private link is no longer active.';
        }
      }

      let restoredHero: HeroCaptureBundle | null = null;
      let restoredHeroState: 'none' | 'captured' | 'stale' = 'none';
      if (options.showPanel && workspace?.capturedHero) {
        const [visualFingerprint, cameraFingerprint] = await Promise.all([
          createVisualProjectFingerprint(project),
          createHeroCameraFingerprint(project.heroCamera),
        ]);
        if (
          workspace.capturedHero.visualFingerprint === visualFingerprint &&
          workspace.capturedHero.cameraFingerprint === cameraFingerprint
        ) {
          restoredHero = (await runtimeRef.current?.captureHeroAssets()) ?? null;
          restoredHeroState = restoredHero ? 'captured' : 'none';
        } else {
          restoredHeroState = 'stale';
          hydrationMessage = 'Capture a new Preview Image because the ride changed.';
        }
      }

      if (token !== publishHydrationToken.current) return false;
      cloudRecordRef.current = hydratedCloud;
      setCloudRecord(hydratedCloud);
      setPublishStatus(statusResult?.ok ? statusResult.value : null);
      setPublishWorkspace(workspace);
      setReadOnlyShare(restoredShare);
      replaceHeroCapture(restoredHero);
      setHeroCaptureState(restoredHeroState);
      setCloudConflict(null);
      if (workspace?.lastSyncedProjectFingerprint) {
        const currentFingerprint = await createCloudSyncFingerprint(project);
        lastCloudSyncedEditorRevision.current =
          currentFingerprint === workspace.lastSyncedProjectFingerprint
            ? editorRevisionAtStart
            : null;
      } else {
        lastCloudSyncedEditorRevision.current = hydratedCloud ? editorRevisionAtStart : null;
      }
      if (token !== publishHydrationToken.current) return false;
      setPublishProject(runtimeRef.current?.getProjectSnapshot() ?? project);
      setPublishMessage(hydrationMessage);
      setPublishHydration('ready');
      return true;
    } catch (error) {
      if (token !== publishHydrationToken.current) return false;
      replaceHeroCapture(null);
      setHeroCaptureState('none');
      setPublishProject(runtimeRef.current?.getProjectSnapshot() ?? project);
      setPublishHydration('recoverable_error');
      setPublishMessage(
        `Your local work is safe. ${
          error instanceof Error ? error.message : 'Publishing details could not be restored.'
        }`,
      );
      return false;
    } finally {
      if (token === publishHydrationToken.current) setPublishBusy(false);
    }
  };

  const saveCloudDraft = async (forcedVersion?: number): Promise<CloudDraftRecord | null> => {
    const runtime = runtimeRef.current;
    if (!runtime) return null;
    const draftState = runtime.snapshot();
    if (!draftState.hasSavedDraft || draftState.hasUnsavedChanges) {
      setPublishMessage('Save Draft before using publishing or private links.');
      return null;
    }
    setPublishBusy(true);
    setPublishMessage(null);
    try {
      return await enqueueCloudWrite(async () => {
        const project = runtime.getProjectSnapshot();
        const currentCloud = cloudRecordRef.current;
        const expectedVersion =
          forcedVersion ??
          (currentCloud?.projectId === project.projectId ? currentCloud.version : null);
        const result = await platform.saveCloudDraft(project, { expectedVersion });
        if (!result.ok) {
          if (result.error.code === 'conflict') {
            const current = result.error.details?.currentVersion;
            setCloudConflict({
              projectId: project.projectId,
              currentVersion: typeof current === 'number' ? current : (expectedVersion ?? 0) + 1,
              detectedAt: new Date().toISOString(),
            });
            setPublishMessage(
              'This cloud draft changed in another session. Choose which copy to keep.',
            );
          } else {
            setPublishMessage(`Your local draft is safe. ${result.error.message}`);
          }
          return null;
        }
        cloudRecordRef.current = result.value;
        setCloudRecord(result.value);
        lastCloudSyncedEditorRevision.current = state.editorRevision;
        setCloudConflict(null);
        if (viewer.kind === 'authenticated') {
          const storedWorkspace = publishWorkspaceRepository
            ? await publishWorkspaceRepository.load(project.projectId).catch(() => null)
            : null;
          const baseWorkspace =
            storedWorkspace?.projectId === project.projectId &&
            storedWorkspace.ownerId === viewer.id
              ? storedWorkspace
              : createEmptyPublishWorkspace(viewer.id, project.projectId);
          await persistPublishWorkspace({
            ...baseWorkspace,
            lastCloudVersion: result.value.version,
            lastSyncedProjectFingerprint: await createCloudSyncFingerprint(project),
            updatedAt: new Date().toISOString(),
          });
        }
        setPublishMessage(
          localQaContext ? 'QA draft saved in this browser.' : 'Cloud draft saved.',
        );
        await refreshCloudDrafts();
        return result.value;
      });
    } finally {
      setPublishBusy(false);
    }
  };

  const openCloudDraft = async (projectId: string): Promise<CloudDraftRecord | null> => {
    setPublishBusy(true);
    setLaunchMessage('Loading cloud draft…');
    try {
      const result = await platform.loadCloudDraft(projectId);
      if (!result.ok) {
        setLaunchMessage(result.error.message);
        return null;
      }
      runtimeRef.current?.openEditor();
      await runtimeRef.current?.loadProject(result.value.project, 'editable_cloud');
      await hydratePublishContext(result.value.project, {
        showPanel: false,
        knownCloudRecord: result.value,
      });
      setVisitor(null);
      setDraftsOpen(false);
      setLaunchMessage(null);
      return result.value;
    } finally {
      setPublishBusy(false);
    }
  };

  const deleteCloudDraft = async (draft: CloudDraftSummary) => {
    setPublishBusy(true);
    try {
      const result = await platform.deleteCloudDraft(draft.projectId, draft.version);
      setPublishMessage(result.ok ? 'Cloud draft deleted.' : result.error.message);
      if (result.ok) {
        if (cloudRecord?.projectId === draft.projectId) setCloudRecord(null);
        await refreshCloudDrafts();
      }
    } finally {
      setPublishBusy(false);
    }
  };

  const captureHero = async () => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    setPublishBusy(true);
    setPublishMessage('Capturing your Preview Image…');
    try {
      const capture = await runtime.captureHeroAssets();
      const project = runtime.getProjectSnapshot();
      const baseWorkspace =
        publishWorkspace?.projectId === project.projectId &&
        publishWorkspace.ownerId === publishWorkspaceOwnerId
          ? publishWorkspace
          : createEmptyPublishWorkspace(publishWorkspaceOwnerId, project.projectId);
      await persistPublishWorkspace({
        ...baseWorkspace,
        capturedHero: {
          visualFingerprint: await createVisualProjectFingerprint(project),
          cameraFingerprint: await createHeroCameraFingerprint(capture.camera),
          capturedAt: new Date().toISOString(),
        },
        updatedAt: new Date().toISOString(),
      });
      replaceHeroCapture(capture);
      setHeroCaptureState('captured');
      setPublishMessage('Preview Image captured.');
    } catch (error) {
      setPublishMessage(
        error instanceof Error ? error.message : 'The Preview Image could not be captured.',
      );
    } finally {
      setPublishBusy(false);
    }
  };

  const submitForReview = async () => {
    if (!heroCapture) {
      setPublishMessage('Capture a Preview Image before submitting.');
      return;
    }
    const saved = await saveCloudDraft();
    if (!saved) return;
    setPublishBusy(true);
    try {
      setPublishMessage('Uploading the Preview Image…');
      const uploaded = await platform.uploadHeroAssets(saved.projectId, {
        hero16x9: heroCapture.hero16x9.blob,
        openGraph: heroCapture.openGraph.blob,
      });
      if (!uploaded.ok) {
        setPublishMessage(uploaded.error.message);
        return;
      }
      setPublishMessage('Submitting this revision for review…');
      const result = await platform.requestPublish({
        projectId: saved.projectId,
        expectedVersion: saved.version,
        heroUpload: uploaded.value,
      });
      if (!result.ok) {
        setPublishMessage(result.error.message);
        return;
      }
      setPublishStatus(result.value);
      setCloudRecord({
        ...saved,
        state: result.value.state,
        pendingRevisionId: result.value.pendingRevisionId,
        currentPublicRevisionId: result.value.currentPublicRevisionId,
        publicCoasterId: result.value.publicCoasterId,
        publicSlug: result.value.publicSlug,
      });
      setPublishMessage(
        'Submitted. Your current public revision stays live while this update is reviewed.',
      );
    } finally {
      setPublishBusy(false);
    }
  };

  const createReadOnlyShare = async () => {
    const saved = await saveCloudDraft();
    if (!saved) return;
    setPublishBusy(true);
    try {
      const result = await platform.createReadOnlyShare(saved.projectId);
      if (!result.ok) {
        setPublishMessage(result.error.message);
        return;
      }
      if (viewer.kind === 'authenticated') {
        const project = runtimeRef.current?.getProjectSnapshot();
        if (project) {
          const storedWorkspace = publishWorkspaceRepository
            ? await publishWorkspaceRepository.load(project.projectId)
            : null;
          const baseWorkspace =
            storedWorkspace?.projectId === project.projectId &&
            storedWorkspace.ownerId === viewer.id
              ? storedWorkspace
              : createEmptyPublishWorkspace(viewer.id, project.projectId);
          await persistPublishWorkspace({
            ...baseWorkspace,
            lastCloudVersion: saved.version,
            lastSyncedProjectFingerprint: await createCloudSyncFingerprint(project),
            share: result.value,
            updatedAt: new Date().toISOString(),
          });
        }
      }
      setReadOnlyShare(result.value);
      setPublishMessage('Secret read-only link created. Anyone with the full link can open it.');
    } finally {
      setPublishBusy(false);
    }
  };

  const revokeReadOnlyShare = async () => {
    if (!readOnlyShare) return;
    setPublishBusy(true);
    try {
      const result = await platform.revokeReadOnlyShare(readOnlyShare.shareId);
      if (result.ok) {
        setReadOnlyShare(null);
        if (publishWorkspace) {
          await persistPublishWorkspace(
            {
              ...publishWorkspace,
              share: null,
              updatedAt: new Date().toISOString(),
            },
            { clearShare: true },
          );
        }
        setPublishMessage('Read-only link revoked.');
      } else setPublishMessage(result.error.message);
    } finally {
      setPublishBusy(false);
    }
  };

  const loadCloudConflict = async () => {
    if (!cloudConflict) return;
    setPublishBusy(true);
    try {
      const result = await platform.loadCloudDraft(cloudConflict.projectId);
      if (!result.ok) {
        setPublishMessage(`Cloud copy could not be loaded: ${result.error.message}`);
        return;
      }
      runtimeRef.current?.openEditor();
      await runtimeRef.current?.loadProject(result.value.project, 'editable_cloud');
      const hydrated = await hydratePublishContext(result.value.project, {
        showPanel: true,
        knownCloudRecord: result.value,
      });
      if (!hydrated) return;
      setCloudConflict(null);
      setPublishMessage(
        `Loaded ${result.value.project.metadata.title} for ${result.value.project.metadata.creator.displayName} · cloud version ${result.value.version}.`,
      );
    } finally {
      setPublishBusy(false);
    }
  };

  const keepLocalConflict = async () => {
    if (!cloudConflict) return;
    await saveCloudDraft(cloudConflict.currentVersion);
  };

  const savePublishingDetails = (details: PublishingDetails) => {
    runtimeRef.current?.setPublishingDetails(details);
    setPublishProject(runtimeRef.current?.getProjectSnapshot() ?? null);
  };

  const remixVisitorCoaster = async () => {
    if (!visitor) return;
    setPublishBusy(true);
    setLaunchMessage('Creating an independent draft…');
    try {
      const result = await platform.remixCoaster(
        visitor.kind === 'public'
          ? { publicCoasterId: visitor.record.publicCoasterId }
          : {
              share: {
                shareId: visitor.record.shareId,
                secret: new URLSearchParams(window.location.hash.slice(1)).get('cap') ?? '',
              },
            },
      );
      if (!result.ok) {
        setLaunchMessage(result.error.message);
        return;
      }
      await runtimeRef.current?.loadProject(result.value.draft.project, 'editable_remix');
      setActiveRemixSource(activePublicCard);
      setCloudRecord(result.value.draft);
      setPublishStatus(null);
      setVisitor(null);
      setActivePublicCard(null);
      referralRide.current = null;
      setLaunchMessage(null);
      history.replaceState(null, '', editorResetPath);
    } finally {
      setPublishBusy(false);
    }
  };

  const approveLocalQaRevision = async () => {
    if (!cloudRecord || !isLocalPhase4QaPlatformAdapter(platform)) return;
    const result = await platform.moderatePending(cloudRecord.projectId, { kind: 'approve' });
    if (result.ok) {
      setPublishStatus(result.value);
      setCloudRecord({
        ...cloudRecord,
        state: result.value.state,
        pendingRevisionId: result.value.pendingRevisionId,
        currentPublicRevisionId: result.value.currentPublicRevisionId,
        publicCoasterId: result.value.publicCoasterId,
        publicSlug: result.value.publicSlug,
      });
      setPublishMessage('Local QA moderation approved this revision.');
      await refreshCloudDrafts();
    } else setPublishMessage(result.error.message);
  };

  const statusTone = useMemo(() => {
    if (state.status.toLowerCase().includes('invalid')) return 'danger';
    if (
      state.status.toLowerCase().includes('unavailable') ||
      state.status.toLowerCase().includes('saving is off') ||
      state.status.toLowerCase().includes("couldn't save") ||
      state.status.toLowerCase().includes('stalled')
    )
      return 'warning';
    return 'neutral';
  }, [state.status]);
  const activeCloudRecord =
    cloudRecord && cloudRecord.projectId === publishProject?.projectId ? cloudRecord : null;
  const activePublishStatus =
    publishStatus && publishStatus.projectId === publishProject?.projectId ? publishStatus : null;
  const activeStatus = activePublishStatus
    ? ownerProjectStatusLabel(activePublishStatus.state)
    : activeCloudRecord
      ? ownerProjectStatusLabel(activeCloudRecord.state)
      : 'local_only';
  const activePublicSlug = activePublishStatus?.publicSlug ?? activeCloudRecord?.publicSlug ?? null;
  const activePublicUrl = activePublicSlug
    ? buildPublicCoasterUrl({
        publicSlug: activePublicSlug,
        publicBaseUrl,
        localQa: localQaContext,
      })
    : null;
  const activeShareUrl = readOnlyShare
    ? buildReadOnlyShareUrl({
        sharePath: readOnlyShare.path,
        publicBaseUrl,
        localQa: localQaContext,
      })
    : null;
  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    const project = runtime.getProjectSnapshot();
    const publicRecord = visitor?.kind === 'public' ? visitor.record : null;
    const visitorPublicUrl = publicRecord
      ? buildPublicCoasterUrl({
          publicSlug: publicRecord.publicSlug,
          publicBaseUrl,
          localQa: localQaContext,
        })
      : null;
    runtime.setMediaShareContext({
      creatorDisplayName:
        publicRecord?.creator.displayName ??
        (visitor?.kind === 'unlisted'
          ? visitor.record.project.metadata.creator.displayName
          : project.metadata.creator.displayName),
      publicCoasterId:
        publicRecord?.publicCoasterId ??
        activePublishStatus?.publicCoasterId ??
        activeCloudRecord?.publicCoasterId ??
        null,
      publicUrl: visitorPublicUrl ?? activePublicUrl,
    });
  }, [
    activeCloudRecord?.publicCoasterId,
    activePublicUrl,
    activePublishStatus?.publicCoasterId,
    localQaContext,
    publicBaseUrl,
    state.fixtureId,
    visitor,
  ]);
  const lastOpenedDraft = state.lastOpenedProjectId
    ? (state.drafts.find(
        (draft) => draft.projectId === state.lastOpenedProjectId && !draft.corrupt,
      ) ?? null)
    : null;
  const draftPendingDeletion = confirmDeleteProjectId
    ? (state.drafts.find((draft) => draft.projectId === confirmDeleteProjectId) ?? null)
    : null;

  const clearPublishContext = () => {
    publishHydrationToken.current += 1;
    setPublishOpen(false);
    setPublishHydration('idle');
    setPublishBusy(false);
    setPublishProject(null);
    setPublishMessage(null);
    setCloudConflict(null);
    cloudRecordRef.current = null;
    setCloudRecord(null);
    setPublishStatus(null);
    setPublishWorkspace(null);
    setReadOnlyShare(null);
    replaceHeroCapture(null);
    setHeroCaptureState('none');
    lastCloudSyncedEditorRevision.current = null;
  };

  const returnToMainMenu = () => {
    clearPublishContext();
    setConfirmExit(false);
    setConfirmPublishSave(false);
    setRenameValue(null);
    setActiveRemixSource(null);
    runtimeRef.current?.returnToMenu();
  };

  const commitPendingProjectName = (): boolean => {
    if (renameValue === null) return true;
    const runtime = runtimeRef.current;
    if (!runtime) return false;
    if (renameValue === state.fixtureTitle) {
      setRenameValue(null);
      return true;
    }
    const renamed = runtime.renameProject(renameValue);
    if (renamed) setRenameValue(null);
    else projectNameRef.current?.focus();
    return renamed;
  };

  const saveCurrentDraft = async (): Promise<boolean> => {
    if (!commitPendingProjectName()) return false;
    const runtime = runtimeRef.current;
    if (!runtime) return false;
    const saved = await runtime.saveNow();
    if (!saved) return false;
    const projectId = runtime.getProjectSnapshot().projectId;
    try {
      const capture = await runtime.captureHeroAssets();
      const preview = await createDraftPreview(capture.hero16x9.blob);
      if (preview) {
        await draftPreviewRepository.save(projectId, preview);
        const nextUrl = URL.createObjectURL(preview);
        setDraftPreviewUrls((current) => {
          const old = current[projectId];
          if (old) URL.revokeObjectURL(old);
          const next = { ...current, [projectId]: nextUrl };
          draftPreviewUrlsRef.current = next;
          return next;
        });
      }
    } catch {
      // A draft remains successfully saved when its optional preview fails.
    }
    return true;
  };

  const openPublishPanel = () => {
    const project = runtimeRef.current?.getProjectSnapshot();
    if (!project) return;
    clearPublishContext();
    setPublishSessionId((value) => value + 1);
    setPublishOpen(true);
    setPublishProject(null);
    setPublishHydration('loading');
    setPublishMessage(null);
    void hydratePublishContext(project, { showPanel: true });
  };

  return (
    <main
      class={`app-shell mode-${state.mode} ride-phase-${state.ride.phase} selection-target-${state.selectionTarget}`}
    >
      <header class="topbar">
        <div class="brand-navigation">
          <div class="brand-lockup">
            <span class="eyebrow">Coaster Builder</span>
            <h1>Buncha Coasters</h1>
          </div>
          {inEditor && !visitor && (
            <nav class="platform-actions" aria-label="Game navigation">
              <Button
                variant="tertiary"
                size="compact"
                icon={ArrowLeft}
                type="button"
                onClick={() => {
                  if (state.hasUnsavedChanges || hasPendingNameChange) setConfirmExit(true);
                  else returnToMainMenu();
                }}
              >
                {UI_COPY.navigation.backToMenu}
              </Button>
              <IconButton
                label={UI_COPY.navigation.help}
                icon={CircleHelp}
                size="compact"
                type="button"
                onClick={() => runtimeRef.current?.setTutorialVisible(true)}
              />
            </nav>
          )}
        </div>
        <div class="topbar-actions">
          {inEditor && !visitor ? (
            <>
              <label class="project-name-field">
                <span class="sr-only">Coaster name</span>
                <input
                  ref={projectNameRef}
                  value={renameValue ?? state.fixtureTitle}
                  maxLength={80}
                  onFocus={() => {
                    if (renameValue === null) setRenameValue(state.fixtureTitle);
                  }}
                  onInput={(event) => setRenameValue(event.currentTarget.value)}
                  onBlur={() => {
                    if (renameValue === null) return;
                    if (renameValue.trim().length === 0) return;
                    if (
                      renameValue === state.fixtureTitle ||
                      runtimeRef.current?.renameProject(renameValue)
                    )
                      setRenameValue(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                  }}
                />
              </label>
              {activeRemixSource && (
                <span class="remix-source-badge">
                  Remix of {activeRemixSource.title} by {activeRemixSource.creator.displayName}
                </span>
              )}
              <StatusPill
                tone={
                  state.saveState === 'error'
                    ? 'danger'
                    : state.saveState === 'clean'
                      ? 'ready'
                      : 'warning'
                }
              >
                <span class={`save-state save-${state.saveState}`} data-testid="save-state">
                  {state.saveState === 'saving'
                    ? 'Saving Draft…'
                    : state.saveState === 'error'
                      ? 'Save Failed'
                      : state.saveState === 'editing'
                        ? state.hasSavedDraft || hasPendingNameChange
                          ? 'Unsaved Changes'
                          : 'Unsaved'
                        : 'Draft Saved'}
                </span>
              </StatusPill>
              <Button
                icon={Save}
                size="compact"
                type="button"
                onClick={() => {
                  setCoordinateRecoveryEpoch((value) => value + 1);
                  void saveCurrentDraft().then((saved) => {
                    if (!saved) projectNameRef.current?.focus();
                  });
                }}
              >
                {UI_COPY.editor.saveDraft}
              </Button>
              <div class="project-history-actions" role="group" aria-label="Project actions">
                <IconButton
                  class="topbar-undo"
                  label="Undo"
                  icon={Undo2}
                  size="compact"
                  type="button"
                  disabled={!state.canUndo}
                  onClick={() => runtimeRef.current?.undo()}
                />
                <IconButton
                  class="topbar-redo"
                  label="Redo"
                  icon={Redo2}
                  size="compact"
                  type="button"
                  disabled={!state.canRedo}
                  onClick={() => runtimeRef.current?.redo()}
                />
                {state.media.available && (
                  <IconButton
                    class="topbar-media-action"
                    label={UI_COPY.media.title}
                    icon={Film}
                    size="compact"
                    type="button"
                    onClick={() => void runtimeRef.current?.openMediaStudio()}
                  />
                )}
                <span class="topbar-ride-actions">
                  <Button
                    variant="primary"
                    size="compact"
                    type="button"
                    disabled={!state.testAllowed}
                    onClick={() => runtimeRef.current?.startRide('first-person')}
                  >
                    {UI_COPY.editor.testRide}
                  </Button>
                  {reactionCamAvailable && (
                    <IconButton
                      label="More ride options"
                      icon={ChevronDown}
                      size="compact"
                      type="button"
                      aria-expanded={rideMenuOpen}
                      onClick={() => setRideMenuOpen((open) => !open)}
                    />
                  )}
                  {rideMenuOpen && reactionCamAvailable && (
                    <div class="bc-popover-menu topbar-ride-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!state.testAllowed}
                        onClick={() => {
                          setRideMenuOpen(false);
                          runtimeRef.current?.openReactionCamSetup('first-person');
                        }}
                      >
                        <Sparkles aria-hidden="true" size={17} />
                        Ride with Reaction Cam
                      </button>
                    </div>
                  )}
                </span>
                <Button
                  variant="primary"
                  size="compact"
                  type="button"
                  onClick={() => {
                    if (!state.hasSavedDraft || state.hasUnsavedChanges || hasPendingNameChange)
                      setConfirmPublishSave(true);
                    else openPublishPanel();
                  }}
                  data-testid="open-publish"
                >
                  {UI_COPY.editor.publish}
                </Button>
                <span class="topbar-overflow">
                  <IconButton
                    label="More project actions"
                    icon={MoreHorizontal}
                    size="compact"
                    type="button"
                    aria-expanded={topbarMenuOpen}
                    onClick={() => setTopbarMenuOpen((open) => !open)}
                  />
                  {topbarMenuOpen && (
                    <div class="bc-popover-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => runtimeRef.current?.setMuted(!state.settings.muted)}
                      >
                        {state.settings.muted ? (
                          <Volume2 aria-hidden="true" size={17} />
                        ) : (
                          <VolumeX aria-hidden="true" size={17} />
                        )}
                        {state.settings.muted ? 'Turn Sound On' : 'Mute Sound'}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setTopbarMenuOpen(false);
                          setConfirmReset(true);
                        }}
                      >
                        <RotateCcw aria-hidden="true" size={17} />
                        Reset Coaster
                      </button>
                    </div>
                  )}
                </span>
              </div>
            </>
          ) : (
            <span class="fixture-name">
              <strong>{visitor ? 'Ride a Coaster' : 'Build a Coaster'}</strong>
            </span>
          )}
          {state.debugVisible && <span class="debug-badge">Debug Build</span>}
        </div>
      </header>

      <section class="world-shell" aria-label="Coaster design and ride viewport">
        <canvas ref={canvasRef} id="coaster-canvas" data-testid="coaster-canvas" />
        <div class="canvas-vignette" aria-hidden="true" />
        {state.ride.photoFlash && (
          <div
            class={`ride-photo-flash ${state.settings.reducedMotion ? 'reduced' : ''}`}
            aria-label="On-ride photo taken"
            role="status"
          />
        )}

        {state.mode === 'menu' && (
          <section
            class={`start-screen social-hub view-${hubView}`}
            data-testid="start-screen"
            aria-labelledby="welcome-title"
          >
            <nav class="hub-navigation" aria-label="Coaster hub">
              <button
                type="button"
                aria-current={hubView !== 'my-coasters'}
                onClick={() => setHubView('home')}
              >
                Build
              </button>
              <button
                type="button"
                aria-current={hubView === 'my-coasters'}
                onClick={() => setHubView('my-coasters')}
              >
                My Coasters
              </button>
            </nav>
            {hubView === 'home' && (
              <div class="start-copy">
                <h2 id="welcome-title">What will you build?</h2>
                {state.lastOpenedProjectId && lastOpenedDraft && (
                  <button
                    data-testid="continue-draft"
                    class="primary-action continue-action"
                    type="button"
                    onClick={() => void runtimeRef.current?.openDraft(state.lastOpenedProjectId!)}
                  >
                    Continue {lastOpenedDraft.title}
                  </button>
                )}
              </div>
            )}
            {hubView === 'home' && (
              <div class="template-grid hub-section-grid" aria-label="Build options">
                <button
                  data-testid="start-new-coaster"
                  class="template-card recommended"
                  type="button"
                  onClick={() => runtimeRef.current?.startTemplate('blank')}
                >
                  <PencilLine aria-hidden="true" size={26} />
                  <strong>Start a New Coaster</strong>
                </button>
                <button
                  data-testid="my-drafts-card"
                  class="template-card"
                  type="button"
                  onClick={() => setDraftsOpen(!draftsOpen)}
                >
                  <FolderOpen aria-hidden="true" size={26} />
                  <strong>My Drafts ({state.drafts.length})</strong>
                </button>
                <button
                  data-testid="browse-remix-card"
                  class="template-card"
                  type="button"
                  onClick={() => setHubView('browse')}
                >
                  <Compass aria-hidden="true" size={26} />
                  <strong>Browse &amp; Remix</strong>
                </button>
              </div>
            )}
            {hubView === 'home' && (
              <div class="start-storage-note">
                <small>
                  Saved on this device ·{' '}
                  {state.persistenceTier === 'durable' ? 'durable storage' : 'limited storage'}
                </small>
              </div>
            )}
            {hubView === 'home' && draftsOpen && (
              <section class="draft-library" data-testid="draft-library" aria-label="My Drafts">
                <div class="drawer-heading">
                  <strong>My Drafts</strong>
                  <CloseButton
                    label="Close drafts"
                    size="compact"
                    type="button"
                    onClick={() => setDraftsOpen(false)}
                  />
                </div>
                {state.drafts.length ? (
                  <div class="draft-list">
                    {state.drafts.map((draft) => (
                      <article class={`draft-card ${draft.corrupt ? 'is-corrupt' : ''}`}>
                        <div class="draft-preview">
                          {draftPreviewUrls[draft.projectId] ? (
                            <img src={draftPreviewUrls[draft.projectId]} alt="" />
                          ) : (
                            <span aria-hidden="true">
                              <i />
                            </span>
                          )}
                        </div>
                        <div>
                          <strong>{draft.title}</strong>
                          <small>
                            {draft.corrupt
                              ? draft.errorMessage
                              : new Date(draft.savedAt).toLocaleString()}
                          </small>
                        </div>
                        <div>
                          <button
                            type="button"
                            disabled={draft.corrupt}
                            onClick={() => void runtimeRef.current?.openDraft(draft.projectId)}
                          >
                            Open
                          </button>
                          <button
                            type="button"
                            disabled={draft.corrupt}
                            onClick={() => void runtimeRef.current?.duplicateDraft(draft.projectId)}
                          >
                            Duplicate
                          </button>
                          <button
                            class="danger-action"
                            type="button"
                            onClick={() => setConfirmDeleteProjectId(draft.projectId)}
                          >
                            Delete
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p>No saved drafts yet.</p>
                )}
                {viewer.kind === 'authenticated' && (
                  <div class="cloud-draft-section">
                    <div class="drawer-heading">
                      <div>
                        <strong>{localQaContext ? 'Local QA drafts' : 'Buncha Cloud'}</strong>
                        <small>{viewer.displayName}</small>
                      </div>
                      <button type="button" onClick={() => void refreshCloudDrafts()}>
                        Refresh
                      </button>
                    </div>
                    {cloudDrafts.length ? (
                      <div class="draft-list" data-testid="cloud-draft-list">
                        {cloudDrafts.map((draft) => (
                          <article class="draft-card">
                            <div>
                              <strong>{draft.title}</strong>
                              <small>
                                {ownerProjectStatusLabel(draft.state).replaceAll('_', ' ')} ·
                                version {draft.version}
                              </small>
                            </div>
                            <div>
                              <button
                                type="button"
                                disabled={publishBusy}
                                onClick={() => void openCloudDraft(draft.projectId)}
                              >
                                Open
                              </button>
                              {draft.publicSlug && (
                                <a
                                  class="secondary-action"
                                  href={buildPublicCoasterUrl({
                                    publicSlug: draft.publicSlug,
                                    publicBaseUrl,
                                    localQa: localQaContext,
                                  })}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View public
                                </a>
                              )}
                              <button
                                type="button"
                                class="danger-action"
                                disabled={publishBusy}
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Delete the cloud draft “${draft.title}”? Your local copy is not deleted.`,
                                    )
                                  )
                                    void deleteCloudDraft(draft);
                                }}
                              >
                                Delete cloud
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p>
                        No {localQaContext ? 'QA' : 'cloud'} drafts yet. Open a coaster and choose
                        Publish to save one.
                      </p>
                    )}
                  </div>
                )}
              </section>
            )}
            {hubView === 'browse' && (
              <BrowsePanel
                page={catalogPage}
                loading={catalogLoading}
                error={catalogError}
                collection={browseCollection}
                remixableOnly={remixableOnly}
                creatorLabel={catalogCreator?.label ?? null}
                pendingIds={socialPendingIds}
                message={catalogMessage}
                onCollection={(collection) => {
                  setCatalogPage(null);
                  setBrowseCollection(collection);
                  if (collection !== 'favorites') setCatalogSort(collection);
                }}
                onRemixable={(enabled) => {
                  setCatalogPage(null);
                  setRemixableOnly(enabled);
                }}
                onClearCreator={() => {
                  setCatalogPage(null);
                  setCatalogCreator(null);
                }}
                onRetry={() => void loadCatalog(false)}
                onLoadMore={() => void loadCatalog(true)}
                onOpen={(card) => void openPublicCard(card)}
                onRemix={(card) => void remixPublicCard(card)}
                onBoost={(card) => void mutateSocial(card, 'boost')}
                onFavorite={(card) => void mutateSocial(card, 'favorite')}
                onCreator={(id, label) => {
                  setCatalogPage(null);
                  setCatalogCreator({ id, label });
                }}
                onShare={(card) => void copyReferralLink(card)}
              />
            )}
            {hubView === 'my-coasters' && (
              <MyCoastersPanel
                viewer={viewer}
                published={myCoasters}
                drafts={state.drafts}
                draftPreviewUrls={draftPreviewUrls}
                tab={myCoastersTab}
                loading={myCoastersLoading}
                error={myCoastersError}
                onTab={setMyCoastersTab}
                onOpenDraft={(projectId) => void runtimeRef.current?.openDraft(projectId)}
                onOpenPublic={(slug) => void openPublicSlug(slug)}
              />
            )}
          </section>
        )}

        {draftPendingDeletion && (
          <Dialog
            id="delete-draft"
            role="alertdialog"
            title="Delete draft?"
            description={`“${draftPendingDeletion.title}” will be permanently deleted from this device.`}
            closeLabel="Cancel"
            onClose={() => setConfirmDeleteProjectId(null)}
          >
            <div>
              <Button type="button" onClick={() => setConfirmDeleteProjectId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                type="button"
                onClick={() => {
                  void runtimeRef.current?.deleteDraft(draftPendingDeletion.projectId);
                  void draftPreviewRepository.delete(draftPendingDeletion.projectId);
                  setDraftPreviewUrls((current) => {
                    const url = current[draftPendingDeletion.projectId];
                    if (url) URL.revokeObjectURL(url);
                    const next = { ...current };
                    delete next[draftPendingDeletion.projectId];
                    draftPreviewUrlsRef.current = next;
                    return next;
                  });
                  setConfirmDeleteProjectId(null);
                }}
              >
                Delete Draft
              </Button>
            </div>
          </Dialog>
        )}

        {inEditor && !visitor && (
          <EditorShell
            state={state}
            runtime={runtimeRef.current}
            coordinateRecoveryEpoch={coordinateRecoveryEpoch}
            confirmReset={confirmReset}
            setConfirmReset={setConfirmReset}
          />
        )}

        {confirmExit && inEditor && !visitor && (
          <Dialog
            id="exit-confirm"
            title="Exit without Saving?"
            description="All unsaved progress will be lost"
            closeLabel="Keep editing"
            onClose={() => setConfirmExit(false)}
          >
            <div>
              <Button
                variant="primary"
                type="button"
                onClick={() => {
                  void saveCurrentDraft().then((saved) => {
                    if (!saved) {
                      projectNameRef.current?.focus();
                      return;
                    }
                    setConfirmExit(false);
                    returnToMainMenu();
                  });
                }}
              >
                Save Draft &amp; Exit
              </Button>
              <Button
                variant="danger"
                type="button"
                onClick={() => {
                  setConfirmExit(false);
                  returnToMainMenu();
                }}
              >
                Exit without Saving
              </Button>
            </div>
          </Dialog>
        )}

        {confirmPublishSave && inEditor && !visitor && (
          <Dialog
            id="publish-save"
            title={UI_COPY.publish.saveGateTitle}
            description={UI_COPY.publish.saveGateDescription}
            closeLabel={UI_COPY.publish.keepEditing}
            onClose={() => setConfirmPublishSave(false)}
          >
            <div>
              <Button
                variant="primary"
                type="button"
                onClick={() => {
                  void saveCurrentDraft().then((saved) => {
                    if (!saved) {
                      projectNameRef.current?.focus();
                      return;
                    }
                    setConfirmPublishSave(false);
                    openPublishPanel();
                  });
                }}
              >
                {UI_COPY.publish.saveAndContinue}
              </Button>
              <Button type="button" onClick={() => setConfirmPublishSave(false)}>
                {UI_COPY.publish.keepEditing}
              </Button>
            </div>
          </Dialog>
        )}

        {inEditor && visitor && (
          <VisitorLaunchPanel
            kind={visitor.kind}
            title={visitor.record.title}
            creatorName={
              visitor.kind === 'public'
                ? visitor.record.creator.displayName
                : visitor.record.project.metadata.creator.displayName
            }
            description={visitor.record.project.metadata.description}
            allowRemix={visitor.record.allowRemix}
            snapshot={state}
            busy={publishBusy}
            message={launchMessage}
            socialCard={visitor.kind === 'public' ? activePublicCard : null}
            onRide={() => runtimeRef.current?.startRide('first-person')}
            onRideWithReaction={() => runtimeRef.current?.openReactionCamSetup('first-person')}
            onRemix={remixVisitorCoaster}
            onBoost={() => {
              if (activePublicCard) void mutateSocial(activePublicCard, 'boost');
            }}
            onFavorite={() => {
              if (activePublicCard) void mutateSocial(activePublicCard, 'favorite');
            }}
            onCopyLink={() => {
              if (activePublicCard) void copyReferralLink(activePublicCard);
            }}
            onExit={() => {
              setVisitor(null);
              setActivePublicCard(null);
              referralRide.current = null;
              setLaunchMessage(null);
              launchAttempted.current = false;
              history.replaceState(null, '', editorResetPath);
              runtimeRef.current?.returnToMenu();
            }}
          />
        )}

        {inEditor && !visitor && (
          <PublishPanel
            open={publishOpen}
            sessionId={publishSessionId}
            snapshot={state}
            project={publishProject}
            viewerLabel={viewer.displayName}
            cloudAvailable={
              viewer.kind === 'authenticated' &&
              platform.getCapability('cloud_drafts').availability === 'available'
            }
            cloudVersion={activeCloudRecord?.version ?? null}
            status={activeStatus as PublishPanelStatus}
            busy={publishBusy}
            message={publishMessage}
            conflictVersion={cloudConflict?.currentVersion ?? null}
            heroPreviewUrl={heroPreviewUrl}
            heroCaptureState={heroCaptureState}
            shareUrl={activeShareUrl}
            publicUrl={activePublicUrl}
            environment={localQaContext ? 'local_qa' : 'buncha'}
            sourceAttribution={
              activeRemixSource
                ? `${activeRemixSource.title} by ${activeRemixSource.creator.displayName}`
                : null
            }
            onClose={() => {
              clearPublishContext();
            }}
            onSaveDetails={savePublishingDetails}
            onSaveDraft={saveCurrentDraft}
            onAutoFrame={() => runtimeRef.current?.frameHeroPreview()}
            onCaptureHero={captureHero}
            onSaveCloud={() => saveCloudDraft().then(() => undefined)}
            onSubmit={submitForReview}
            onCreateShare={createReadOnlyShare}
            onRevokeShare={revokeReadOnlyShare}
            onLoadCloudConflict={loadCloudConflict}
            onKeepLocalConflict={keepLocalConflict}
            onRefreshStatus={() =>
              activeCloudRecord
                ? refreshPublishStatus(activeCloudRecord.projectId, true).then(() => undefined)
                : Promise.resolve()
            }
          />
        )}

        {publishOpen &&
          state.debugVisible &&
          cloudRecord &&
          ownerProjectStatusLabel(cloudRecord.state) === 'pending_review' &&
          isLocalPhase4QaPlatformAdapter(platform) && (
            <button
              type="button"
              class="qa-moderation-action"
              onClick={() => void approveLocalQaRevision()}
            >
              Approve local QA revision
            </button>
          )}

        {launchMessage && !visitor && state.mode === 'menu' && (
          <div class="launch-message" role="alert">
            <strong>Coaster unavailable</strong>
            <span>{launchMessage}</span>
            <button
              type="button"
              onClick={() => {
                setLaunchMessage(null);
                history.replaceState(null, '', editorResetPath);
              }}
            >
              Browse coasters
            </button>
          </div>
        )}

        {inRide && (
          <>
            {reactionUi && <reactionUi.LiveReactionPane state={state.reaction} />}
            <div class="ride-telemetry" aria-label="Live ride telemetry">
              <div>
                <span>Front car speed</span>
                <strong data-testid="speed-value">{mph(state.telemetry.speedMph)}</strong>
              </div>
              <div>
                <span>Front car vertical G</span>
                <strong>{force(state.telemetry.verticalG)}</strong>
              </div>
              <div>
                <span>Front car lateral G</span>
                <strong>{force(state.telemetry.lateralG)}</strong>
              </div>
              <div>
                <span>Track</span>
                <strong>{state.ride.progressPercent.toFixed(0)}%</strong>
              </div>
            </div>
            <div class={`section-status section-${state.telemetry.activeSectionType ?? 'normal'}`}>
              <span>Track section</span>
              <strong data-testid="active-section">{state.telemetry.activeSectionLabel}</strong>
            </div>
            {waitingForDispatch && (
              <div
                class="dispatch-status"
                role="status"
                aria-live="polite"
                data-testid="dispatch-status"
              >
                <span>Station</span>
                <strong>
                  {state.ride.dispatchSecondsRemaining > 0
                    ? `Departing in ${Math.max(1, Math.ceil(state.ride.dispatchSecondsRemaining))}`
                    : 'Dispatching train...'}
                </strong>
              </div>
            )}
            {state.mode === 'replay' ? (
              <div class="ride-controls replay-controls" data-testid="replay-controls">
                <div>
                  <strong>Cinematic Replay</strong>
                  <span>{state.replay.progressPercent.toFixed(0)}%</span>
                </div>
                <button type="button" onClick={() => runtimeRef.current?.toggleReplayPaused()}>
                  {state.replay.paused ? 'Play' : 'Pause'}
                </button>
                <TestSpeedControl value={state.settings.testSpeed} runtime={runtimeRef.current} />
                <button
                  class="secondary-action"
                  type="button"
                  onClick={() => runtimeRef.current?.returnToEditor()}
                >
                  {visitor ? 'Return to coaster' : 'Back to Build'}
                </button>
              </div>
            ) : state.ride.phase === 'running' ? (
              <div class="ride-controls">
                <div class="camera-toggle" role="group" aria-label="Ride camera">
                  <button
                    type="button"
                    aria-pressed={camera === 'first-person'}
                    onClick={() => runtimeRef.current?.setRideCamera('first-person')}
                  >
                    First person
                  </button>
                  <button
                    type="button"
                    aria-pressed={camera === 'third-person'}
                    onClick={() => runtimeRef.current?.setRideCamera('third-person')}
                  >
                    Third person
                  </button>
                </div>
                <TestSpeedControl
                  value={state.settings.testSpeed}
                  runtime={runtimeRef.current}
                  locked={state.reaction.rideSpeedLocked}
                />
                <button
                  class="secondary-action"
                  type="button"
                  onClick={() => runtimeRef.current?.returnToEditor()}
                >
                  {visitor ? 'Return to coaster' : 'Back to Build'}
                </button>
                {state.reaction.phase === 'recording' && (
                  <button
                    class="danger-action"
                    type="button"
                    onClick={() => void runtimeRef.current?.stopAndDiscardReaction()}
                  >
                    Stop &amp; Discard Reaction
                  </button>
                )}
                <details class="ride-settings">
                  <summary>Comfort &amp; Sound</summary>
                  <label>
                    <span>Field of view</span>
                    <input
                      type="range"
                      aria-label="Field of view"
                      min="50"
                      max="75"
                      value={state.settings.cameraFov}
                      onInput={(event) =>
                        runtimeRef.current?.setCameraFov(Number(event.currentTarget.value))
                      }
                    />
                  </label>
                  <label>
                    <span>Camera motion</span>
                    <input
                      type="range"
                      aria-label="Camera motion"
                      min="0"
                      max="1"
                      step="0.05"
                      value={state.settings.cameraShake}
                      onInput={(event) =>
                        runtimeRef.current?.setCameraShake(Number(event.currentTarget.value))
                      }
                    />
                  </label>
                  <label class="toggle-row">
                    <input
                      type="checkbox"
                      checked={state.settings.reducedMotion}
                      onChange={(event) =>
                        runtimeRef.current?.setReducedMotion(event.currentTarget.checked)
                      }
                    />
                    <span>Reduced motion</span>
                  </label>
                  <label class="toggle-row">
                    <input
                      type="checkbox"
                      checked={state.settings.muted}
                      onChange={(event) =>
                        runtimeRef.current?.setMuted(event.currentTarget.checked)
                      }
                    />
                    <span>Mute</span>
                  </label>
                </details>
              </div>
            ) : (
              <section
                class="ride-result"
                aria-labelledby="ride-result-title"
                data-testid="ride-result"
              >
                <span class="eyebrow">Test Result</span>
                <h2 id="ride-result-title">
                  {state.ride.phase === 'completed'
                    ? 'Track Complete!'
                    : state.ride.phase === 'arrival_failed'
                      ? 'Station Arrival Failed'
                      : 'Ride Stalled'}
                </h2>
                <p>
                  {state.ride.phase === 'completed'
                    ? 'Your train finished the circuit and reached the station.'
                    : state.ride.phase === 'arrival_failed'
                      ? 'The train passed through the station too fast. Strengthen the final brakes.'
                      : 'Lower the next hill or add more speed, then try again.'}
                </p>
                <div
                  class={`intensity-callout intensity-${state.ride.intensity.level}`}
                  data-testid="intensity-result"
                >
                  <strong>{state.ride.intensity.label} ride</strong>
                  <span>{state.ride.intensity.message}</span>
                  <small>
                    Strongest moment around {state.ride.intensity.progressPercent.toFixed(0)}%.
                  </small>
                </div>
                <dl>
                  <div>
                    <dt>Top speed</dt>
                    <dd data-testid="result-top-speed">{mph(state.telemetry.peakSpeedMph)}</dd>
                  </div>
                  <div>
                    <dt>Positive G</dt>
                    <dd data-testid="result-positive-g">{force(state.ride.peakVerticalG)}</dd>
                    <small>Near {state.ride.peakVerticalProgressPercent.toFixed(0)}%</small>
                  </div>
                  <div>
                    <dt>Lowest G</dt>
                    <dd data-testid="result-negative-g">{force(state.ride.minVerticalG)}</dd>
                    <small>Near {state.ride.minVerticalProgressPercent.toFixed(0)}%</small>
                  </div>
                  <div>
                    <dt>Peak lateral G</dt>
                    <dd data-testid="result-lateral-g">{force(state.ride.peakLateralG)}</dd>
                    <small>Near {state.ride.peakLateralProgressPercent.toFixed(0)}%</small>
                  </div>
                  <div>
                    <dt>Track reached</dt>
                    <dd>{state.ride.progressPercent.toFixed(0)}%</dd>
                  </div>
                </dl>
                <div class="ride-result-actions">
                  {state.ride.phase === 'completed' && !state.media.requiresNewRide && (
                    <Button
                      class="ride-result-primary"
                      variant="primary"
                      type="button"
                      onClick={() => void runtimeRef.current?.openMediaStudio()}
                    >
                      {UI_COPY.media.title}
                    </Button>
                  )}
                  {state.ride.phase === 'completed' && state.media.requiresNewRide && (
                    <p class="ride-media-deleted" role="status">
                      Ride files deleted. Complete another ride to create new ones.
                    </p>
                  )}
                  <Button
                    class="ride-result-secondary"
                    variant={state.ride.phase === 'completed' ? 'secondary' : 'primary'}
                    type="button"
                    onClick={() => runtimeRef.current?.restartRide(camera)}
                  >
                    Ride Again
                  </Button>
                  <button
                    class="ride-result-back"
                    type="button"
                    onClick={() => runtimeRef.current?.returnToEditor()}
                  >
                    {visitor ? 'Return to coaster' : 'Back to Build'}
                  </button>
                  {(state.replay.available || reactionCamAvailable) && (
                    <span class="ride-result-overflow">
                      <IconButton
                        label="More ride actions"
                        icon={MoreHorizontal}
                        type="button"
                        aria-expanded={rideResultMenuOpen}
                        onClick={() => setRideResultMenuOpen((open) => !open)}
                      />
                      {rideResultMenuOpen && (
                        <div class="bc-popover-menu" role="menu">
                          {state.replay.available && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setRideResultMenuOpen(false);
                                runtimeRef.current?.startReplay();
                              }}
                            >
                              {state.ride.phase === 'completed' ? 'Full Replay' : 'Review Ride'}
                            </button>
                          )}
                          {reactionCamAvailable && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setRideResultMenuOpen(false);
                                runtimeRef.current?.openReactionCamSetup(camera);
                              }}
                            >
                              Ride Again with Reaction Cam
                            </button>
                          )}
                        </div>
                      )}
                    </span>
                  )}
                </div>
                {state.reaction.rawReviewUrl && (
                  <section class="reaction-result-review" aria-label="Local Reaction Cam review">
                    <div>
                      <strong>Reaction Cam is ready</strong>
                      <span>{state.reaction.message}</span>
                    </div>
                    <video
                      src={state.reaction.rawReviewUrl}
                      controls
                      playsInline
                      preload="metadata"
                    />
                    <button
                      class="danger-action"
                      type="button"
                      onClick={() => void runtimeRef.current?.discardReactionRecording()}
                    >
                      Discard Reaction
                    </button>
                  </section>
                )}
              </section>
            )}
          </>
        )}

        {state.debugVisible && (
          <aside class="debug-panel" data-testid="debug-panel">
            <span class="eyebrow">Debug Stats</span>
            <dl>
              <div>
                <dt>Quality</dt>
                <dd>{state.diagnostics.qualityTier}</dd>
              </div>
              <div>
                <dt>Samples</dt>
                <dd>{state.diagnostics.sampleCount}</dd>
              </div>
              <div>
                <dt>Changed spans</dt>
                <dd>{state.diagnostics.changedSpans}</dd>
              </div>
              <div>
                <dt>Rebuild</dt>
                <dd>{state.diagnostics.rebuildMs.toFixed(1)} ms</dd>
              </div>
              <div>
                <dt>Track length</dt>
                <dd>{state.diagnostics.trackLengthM.toFixed(1)} m</dd>
              </div>
              <div>
                <dt>Geometry rev</dt>
                <dd>{state.diagnostics.geometryRevision}</dd>
              </div>
              <div>
                <dt>Frame p50 / p95</dt>
                <dd>
                  {state.diagnostics.frameMsP50.toFixed(1)} /{' '}
                  {state.diagnostics.frameMsP95.toFixed(1)} ms
                </dd>
              </div>
              <div>
                <dt>Input p50 / p95</dt>
                <dd>
                  {state.diagnostics.inputMsP50.toFixed(1)} /{' '}
                  {state.diagnostics.inputMsP95.toFixed(1)} ms
                </dd>
              </div>
              <div>
                <dt>Renderer</dt>
                <dd>
                  {state.diagnostics.rendererGeometries} geo / {state.diagnostics.rendererTextures}{' '}
                  tex
                </dd>
              </div>
              <div>
                <dt>Ride cycles / trace</dt>
                <dd>
                  {state.diagnostics.rideCycles} / {state.diagnostics.retainedTraceSamples}
                </dd>
              </div>
              <div>
                <dt>Media trace / URLs</dt>
                <dd>
                  {state.diagnostics.retainedMediaSamples} / {state.diagnostics.mediaArtifactUrls}
                  {state.diagnostics.mediaExportActive ? ' · exporting' : ''}
                </dd>
              </div>
              <div>
                <dt>Reaction tracks / recorder</dt>
                <dd>
                  {state.diagnostics.reactionActiveTracks} /{' '}
                  {state.diagnostics.reactionRecorderActive ? 'active' : 'off'}
                </dd>
              </div>
              <div>
                <dt>Reaction retained</dt>
                <dd>{Math.ceil(state.diagnostics.reactionRetainedBytes / 1024)} KiB</dd>
              </div>
              <div>
                <dt>Validation active / queued</dt>
                <dd>
                  {state.diagnostics.validationActive} / {state.diagnostics.validationQueued}
                </dd>
              </div>
              <div>
                <dt>Audio voices / listeners</dt>
                <dd>
                  {state.diagnostics.audioActiveVoices} / {state.diagnostics.listenerCount}
                </dd>
              </div>
              <div>
                <dt>Rail seam</dt>
                <dd data-testid="rail-seam-gap">
                  {state.diagnostics.maxRailSeamGapM.toExponential(1)} m
                </dd>
              </div>
              <div>
                <dt>Seam angle</dt>
                <dd>{state.diagnostics.maxRailSeamAngleDegrees.toFixed(3)}°</dd>
              </div>
              <div>
                <dt>Max tie gap</dt>
                <dd>{state.diagnostics.maxTieSpacingM.toFixed(3)} m</dd>
              </div>
              <div>
                <dt>Local draft</dt>
                <dd>{state.persistenceAvailable ? 'available' : 'unavailable'}</dd>
              </div>
              <div>
                <dt>Cloud writes</dt>
                <dd>
                  {localQaContext
                    ? 'local QA simulation'
                    : platform.getCapability('cloud_drafts').availability === 'available'
                      ? 'available'
                      : 'unavailable (fail-soft)'}
                </dd>
              </div>
            </dl>
            <div class="debug-fixtures" role="group" aria-label="Debug coaster fixture">
              <button type="button" onClick={() => runtimeRef.current?.loadDebugFixture('systems')}>
                Systems Circuit
              </button>
              <button type="button" onClick={() => runtimeRef.current?.loadDebugFixture('stall')}>
                Stall Test
              </button>
              <button type="button" onClick={() => runtimeRef.current?.loadDebugFixture('extreme')}>
                Extreme Warning Test
              </button>
            </div>
            {(state.mode === 'ride-first-person' || state.mode === 'ride-third-person') && (
              <button
                type="button"
                data-testid="complete-ride-diagnostics"
                onClick={() => runtimeRef.current?.completeRideForDiagnostics()}
              >
                Skip to ride result (QA)
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                void runtimeRef.current
                  ?.captureEvidence()
                  .then((blob) => downloadBlob(blob, 'buncha-coasters-phase3-evidence.png'))
              }
            >
              Capture frame
            </button>
          </aside>
        )}

        {state.media.open && runtimeRef.current && (
          <MediaStudio
            state={state.media}
            runtime={runtimeRef.current}
            title={state.fixtureTitle}
            ReactionOptions={reactionUi?.MediaReactionOptions}
          />
        )}

        {state.reaction.setupOpen && runtimeRef.current && reactionUi && (
          <reactionUi.ReactionCamSetup state={state.reaction} runtime={runtimeRef.current} />
        )}

        <div class={`status-toast status-${statusTone}`} role="status">
          {state.status}
        </div>
      </section>
    </main>
  );
}
